// src/pages/WeeklyActivityFront.js
// 活動前台：支援月/週切換，預設週模式，上方導航固定
// 支援 dark / light 主題（/weekly-activity-w 使用淺色系）
// 支援深連結：/weekly-activity/:activityId 自動載入並開啟該活動

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Popup from '../components/Popup';
import { formatActivityTimeRange } from '../lib/activityVenueTimes';
import { VENUE_INTRO_URL } from '../constants/venueLinks';
import { applyActivityShareMeta, resetActivityShareMeta } from '../lib/activityShareMeta';

function pickContentText(zh, en) {
  let lang = 'zh';
  try {
    const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const fromUrl = String(sp.get('lang') || sp.get('lng') || '').trim().toLowerCase();
    if (fromUrl === 'en' || fromUrl === 'eng' || fromUrl === 'english') lang = 'en';
    else if (fromUrl === 'zh' || fromUrl.startsWith('zh')) lang = 'zh';
    else lang = String(localStorage.getItem('venue_i18n_lang') || 'zh');
  } catch (_) { /* ignore */ }
  if (String(lang).toLowerCase().startsWith('en') && String(en || '').trim()) return String(en);
  return zh == null ? '' : String(zh);
}

const DAY_NAMES_SHORT = ['一', '二', '三', '四', '五', '六', '日'];
const DAY_NAMES = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];

function toLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toLocalYm(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function getMondayOfWeek(weekStart) {
  const today = new Date();
  if (weekStart && /^\d{4}-\d{2}-\d{2}$/.test(weekStart)) {
    const d = new Date(weekStart + 'T12:00:00');
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return toLocalYmd(d);
  }
  const day = today.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  return toLocalYmd(monday);
}

function getWeekDates(mondayYmd) {
  const base = new Date(mondayYmd + 'T12:00:00');
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return toLocalYmd(d);
  });
}

function getMonthCalendarDates(ym) {
  const [year, month] = ym.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const prefixDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
  const totalCells = Math.ceil((prefixDays + daysInMonth) / 7) * 7;
  const dates = [];
  const startDate = new Date(year, month - 1, 1 - prefixDays);
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    dates.push({
      ymd: toLocalYmd(d),
      day: d.getDate(),
      isCurrentMonth: d.getMonth() === month - 1,
    });
  }
  return dates;
}

// 週模式日期：只顯示 xx/xx，不含括號週次
function formatWeekDate(ymd) {
  const d = new Date(ymd + 'T12:00:00');
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${m}/${day}`;
}

function formatMonthLabel(ym) {
  const [year, month] = ym.split('-');
  return `${year} 年 ${parseInt(month, 10)} 月`;
}

const DEFAULT_COLOR = '#4ade80';

// 將文字中的 URL 轉成可點擊的 <a> 標籤
function Linkify({ text, style, linkColor = '#60a5fa' }) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return (
    <span style={style}>
      {parts.map((part, i) =>
        urlRegex.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: 'underline', wordBreak: 'break-all' }}>
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </span>
  );
}

// ====== 主題色彩 ======
function getThemeTokens(isDark) {
  if (isDark) return {
    pageBg: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    headerBg: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
    headerBorder: 'rgba(255,255,255,.15)',
    headerShadow: 'none',
    textPrimary: '#fff',
    textSecondary: 'rgba(255,255,255,.85)',
    textMuted: 'rgba(255,255,255,.7)',
    textDim: 'rgba(255,255,255,.5)',
    cardBg: 'rgba(255,255,255,.08)',
    cardBorder: 'rgba(255,255,255,.12)',
    cardBgHover: 'rgba(255,255,255,.12)',
    btnBg: 'rgba(255,255,255,.1)',
    btnBorder: 'rgba(255,255,255,.3)',
    btnActiveBg: 'rgba(255,255,255,.25)',
    btnActiveBorder: 'rgba(255,255,255,.5)',
    todayCellBg: 'rgba(59,130,246,.35)',
    todayBorder: 'rgba(59,130,246,.7)',
    activityCellBg: 'rgba(255,255,255,.12)',
    emptyCellBg: 'rgba(255,255,255,.05)',
    cellBorder: 'rgba(255,255,255,.1)',
    todayAccent: '#4ade80',
    legendColor: 'rgba(255,255,255,.85)',
    loadingColor: 'rgba(255,255,255,.8)',
    linkColor: '#60a5fa',
    activityTagBg: (color) => `${color}55`,
    popupBtnBg: '#fff',
    popupBtnBorder: '#ccc',
    popupBtnColor: 'inherit',
    popupTextColor: '#555',
    popupDetailBg: '#f5f5f5',
  };
  // 淺色系（會員卡色系）
  return {
    pageBg: 'linear-gradient(180deg, #faf7f2 0%, #f5efe6 50%, #efe8dc 100%)',
    headerBg: 'linear-gradient(180deg, #faf7f2 0%, #f0e8da 100%)',
    headerBorder: 'rgba(94,58,26,.12)',
    headerShadow: '0 2px 8px rgba(94,58,26,.08)',
    textPrimary: '#3d2510',
    textSecondary: '#5e3a1a',
    textMuted: '#8B6F47',
    textDim: '#a89070',
    cardBg: 'rgba(255,255,255,.75)',
    cardBorder: 'rgba(94,58,26,.1)',
    cardBgHover: 'rgba(255,255,255,.9)',
    btnBg: 'rgba(94,58,26,.06)',
    btnBorder: 'rgba(94,58,26,.2)',
    btnActiveBg: 'rgba(94,58,26,.15)',
    btnActiveBorder: 'rgba(94,58,26,.4)',
    todayCellBg: 'rgba(139,111,71,.12)',
    todayBorder: 'rgba(139,111,71,.5)',
    activityCellBg: 'rgba(139,111,71,.06)',
    emptyCellBg: 'rgba(0,0,0,.02)',
    cellBorder: 'rgba(94,58,26,.08)',
    todayAccent: '#28a745',
    legendColor: '#5e3a1a',
    loadingColor: '#8B6F47',
    linkColor: '#2563eb',
    activityTagBg: (color) => `${color}30`,
    popupBtnBg: '#fff',
    popupBtnBorder: '#C4A484',
    popupBtnColor: '#5e3a1a',
    popupTextColor: '#5e3a1a',
    popupDetailBg: '#faf7f2',
  };
}

const THEME_STORAGE_KEY = 'wa-front-theme'; // localStorage key

export default function WeeklyActivityFront({ theme: themeProp }) {
  const { activityId } = useParams();

  // 初始：先看 localStorage，再看 prop，預設 dark
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved === 'light') return false;
      if (saved === 'dark') return true;
    } catch (_) { /* SSR / 隱私模式 */ }
    return themeProp !== 'light';
  });
  const T = useMemo(() => getThemeTokens(isDark), [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem(THEME_STORAGE_KEY, next ? 'dark' : 'light'); } catch (_) {}
      return next;
    });
  }, []);

  const [viewMode, setViewMode] = useState('week'); // 預設週模式
  const [currentMonth, setCurrentMonth] = useState(() => toLocalYm(new Date()));
  const [weekStart, setWeekStart] = useState(() => getMondayOfWeek());
  const [activities, setActivities] = useState([]);
  const [venueColorLegend, setVenueColorLegend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState(null);

  useEffect(() => {
    const act = selectedActivity;
    if (!act?.name && !act?.NAME) {
      if (!activityId) resetActivityShareMeta();
      return undefined;
    }
    const title = pickContentText(act.name || act.NAME, act.name_en || act.NAME_EN);
    const detail = pickContentText(act.detail || act.DETAIL, act.detail_en);
    const date = act.activity_date || act.ACTIVITY_DATE || '';
    const time = String(act.activity_time || act.ACTIVITY_TIME || '').slice(0, 5);
    const venue = pickContentText(act.venue || act.VENUE, act.venue_en || act.VENUE_EN);
    const descFallback = [date, time, venue].filter(Boolean).join(' · ');
    const images = act.images || act.IMAGES;
    const firstImg = Array.isArray(images) && images.length ? images[0] : '';
    applyActivityShareMeta({
      title,
      description: String(detail || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200) || descFallback,
      image: typeof firstImg === 'string' ? firstImg : (firstImg?.url || ''),
      url: typeof window !== 'undefined' ? window.location.href : '',
    });
    return () => {
      if (!activityId) resetActivityShareMeta();
    };
  }, [selectedActivity, activityId]);

  const loadWeek = useCallback(async (start) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/weekly-activity?weekStart=${start}`);
      const data = await res.json();
      setActivities(data.success ? data.activities || [] : []);
      if (data.venueColorLegend) setVenueColorLegend(data.venueColorLegend);
    } catch (e) { console.error(e); setActivities([]); }
    finally { setLoading(false); }
  }, []);

  const loadMonth = useCallback(async (ym) => {
    setLoading(true);
    try {
      const [year, month] = ym.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      const startDayOfWeek = firstDay.getDay();
      const prefixDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
      const totalCells = Math.ceil((prefixDays + lastDay.getDate()) / 7) * 7;
      const startDate = new Date(year, month - 1, 1 - prefixDays);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + totalCells - 1);
      const res = await fetch(`/api/weekly-activity?weekStart=${toLocalYmd(startDate)}&weekEnd=${toLocalYmd(endDate)}`);
      const data = await res.json();
      setActivities(data.success ? data.activities || [] : []);
      if (data.venueColorLegend) setVenueColorLegend(data.venueColorLegend);
    } catch (e) { console.error(e); setActivities([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (viewMode === 'week') loadWeek(weekStart);
    else loadMonth(currentMonth);
  }, [viewMode, weekStart, currentMonth, loadWeek, loadMonth]);

  const goPrevWeek = () => { const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() - 7); setWeekStart(toLocalYmd(d)); };
  const goNextWeek = () => { const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() + 7); setWeekStart(toLocalYmd(d)); };
  const goThisWeek = () => setWeekStart(getMondayOfWeek());
  const goPrevMonth = () => { const [y, m] = currentMonth.split('-').map(Number); setCurrentMonth(toLocalYm(new Date(y, m - 2, 1))); };
  const goNextMonth = () => { const [y, m] = currentMonth.split('-').map(Number); setCurrentMonth(toLocalYm(new Date(y, m, 1))); };
  const goThisMonth = () => setCurrentMonth(toLocalYm(new Date()));

  const activitiesByDate = useMemo(() => {
    const map = {};
    activities.forEach(a => {
      const d = a.activity_date;
      if (!map[d]) map[d] = [];
      map[d].push(a);
    });
    Object.keys(map).forEach(ymd => {
      map[ymd].sort((a, b) => (a.activity_time || '').localeCompare(b.activity_time || ''));
    });
    return map;
  }, [activities]);

  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);
  const monthCalendar = useMemo(() => getMonthCalendarDates(currentMonth), [currentMonth]);
  const todayYmd = useMemo(() => toLocalYmd(new Date()), []);

  const openDetail = async (activity) => {
    setSelectedActivity(activity);
    
    // 更新瀏覽器 URL（不重新載入頁面）
    const isLight = window.location.pathname.includes('weekly-activity-w');
    const basePath = isLight ? '/weekly-activity-w' : '/weekly-activity';
    window.history.pushState(
      { activityId: activity.id },
      '',
      `${basePath}/${activity.id}`
    );
    
    if (activity.images && activity.images.length > 0) return;
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/weekly-activity/${activity.id}`);
      const data = await res.json();
      if (data.success && data.activity) {
        setSelectedActivity((prev) => ({
          ...(prev || activity),
          ...data.activity,
          registrationSlug: (prev || activity).registrationSlug || data.activity.registrationSlug || null,
          registrationProductId: (prev || activity).registrationProductId || data.activity.registrationProductId || null,
          venue_color: (prev || activity).venue_color || data.activity.venue_color,
          venue_en: (prev || activity).venue_en || data.activity.venue_en,
          name_en: (prev || activity).name_en || data.activity.name_en,
        }));
      }
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const getVenueColor = (a) => a.venue_color || DEFAULT_COLOR;

  // === 複製活動連結 ===
  const copyActivityLink = useCallback(() => {
    // 直接複製當前瀏覽器 URL（已包含活動 ID）
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      alert('已複製活動連結');
    }).catch(() => {
      alert('複製失敗，請手動複製：' + currentUrl);
    });
  }, []);

  // === 關閉活動詳情並還原 URL ===
  const closeDetail = useCallback(() => {
    setSelectedActivity(null);
    
    // 還原 URL（移除活動 ID）
    const isLight = window.location.pathname.includes('weekly-activity-w');
    const basePath = isLight ? '/weekly-activity-w' : '/weekly-activity';
    window.history.pushState({}, '', basePath);
  }, []);

  // === 監聽瀏覽器返回按鈕 ===
  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/\/weekly-activity(-w)?\/(\d+)$/);
      if (!match) {
        // URL 沒有活動 ID，關閉彈窗
        setSelectedActivity(null);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // === 深連結：載入單一活動並切換到對應日期 ===
  useEffect(() => {
    if (!activityId) return;
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDeepLinkError(null);
      try {
        const res = await fetch(`/api/weekly-activity/${activityId}`);
        if (!res.ok) {
          if (res.status === 404) {
            setDeepLinkError('活動不存在或已下架');
          } else {
            setDeepLinkError(`載入失敗（HTTP ${res.status}）`);
          }
          return;
        }
        const data = await res.json();
        if (!data.success || !data.activity) {
          setDeepLinkError('活動資料載入失敗');
          return;
        }
        if (cancelled) return;
        const activity = data.activity;
        // 根據活動日期切換到對應月份或週
        if (activity.activity_date) {
          const actDate = new Date(activity.activity_date + 'T12:00:00');
          if (viewMode === 'week') {
            // 計算該日期所在週的週一
            const day = actDate.getDay();
            const diff = day === 0 ? -6 : 1 - day;
            const monday = new Date(actDate);
            monday.setDate(actDate.getDate() + diff);
            setWeekStart(toLocalYmd(monday));
          } else {
            setCurrentMonth(toLocalYm(actDate));
          }
        }
        // 開啟詳情彈窗
        setSelectedActivity(activity);
      } catch (e) {
        console.error('[deep-link]', e);
        if (!cancelled) setDeepLinkError('網路錯誤，請稍後再試');
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activityId, viewMode]);

  // === 樣式（主題感知） ===
  const pageStyle = {
    minHeight: '100vh',
    background: T.pageBg,
    color: T.textSecondary,
    fontFamily: 'system-ui, "Microsoft JhengHei", sans-serif',
    display: 'flex',
    flexDirection: 'column',
  };

  const stickyHeaderStyle = {
    position: 'sticky', top: 0, zIndex: 100,
    background: T.headerBg,
    padding: '12px 12px 10px',
    borderBottom: `1px solid ${T.headerBorder}`,
    boxShadow: T.headerShadow,
  };

  const mainTitleStyle = { fontSize: 22, fontWeight: 700, margin: '0 0 8px 0', color: T.textPrimary, textAlign: 'center' };

  // 導航行：左邊標題+上下周/月按鈕，右邊月/週切換（大按鈕）
  const headerRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 };

  const subTitleStyle = { fontSize: 18, fontWeight: 700, margin: 0, color: T.textPrimary };

  const navBtn = {
    padding: '8px 14px', borderRadius: 10,
    border: `1px solid ${T.btnBorder}`,
    background: T.btnBg,
    color: T.textPrimary,
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    transition: 'all .15s',
  };

  const navBtnActive = { ...navBtn, background: T.btnActiveBg, borderColor: T.btnActiveBorder };

  // 月/週切換：膠囊樣式，帶顏色
  const toggleBtn = {
    padding: '6px 18px', borderRadius: 999,
    border: 'none',
    background: isDark ? 'rgba(255,255,255,.12)' : 'rgba(94,58,26,.08)',
    color: T.textMuted,
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all .2s',
    lineHeight: 1.3,
  };
  const toggleBtnActive = {
    ...toggleBtn,
    background: isDark ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #8B6F47 0%, #6B5B4B 100%)',
    color: '#fff',
    boxShadow: isDark ? '0 2px 8px rgba(102,126,234,.4)' : '0 2px 8px rgba(139,111,71,.3)',
  };

  const contentStyle = { flex: 1, padding: '8px 4px 60px', overflowY: 'auto' };

  // 週模式 — 用 margin 補回較寬的內距
  const dayCardStyle = { background: T.cardBg, borderRadius: 12, padding: '10px 12px', marginBottom: 8, marginLeft: 8, marginRight: 8, border: `1px solid ${T.cardBorder}` };
  const dayTitleStyle = { fontSize: 16, fontWeight: 700, marginBottom: 6, color: T.textPrimary };
  const weekActivityBlockStyle = { marginTop: 6, padding: '8px 10px', background: T.cardBgHover, borderRadius: 8, cursor: 'pointer', transition: 'background .2s' };
  const activityRowStyle = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px' };

  // 月曆 — 極窄 gap + padding，讓格子內文字區域最大化（手機上每行約 5 中文字）
  const calendarGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 8 };
  const calendarHeaderCell = { textAlign: 'center', fontSize: 12, fontWeight: 700, color: T.textMuted, padding: '4px 0' };
  const calendarCell = (isCurrentMonth, isToday, hasActivity) => ({
    minHeight: 48,
    background: isToday ? T.todayCellBg : hasActivity ? T.activityCellBg : T.emptyCellBg,
    borderRadius: 4,
    padding: '2px 1px',
    cursor: 'default',
    opacity: isCurrentMonth ? 1 : 0.4,
    border: isToday ? `2px solid ${T.todayBorder}` : `1px solid ${T.cellBorder}`,
    transition: 'background .2s',
    overflow: 'hidden',
  });
  const calendarDayNum = { fontSize: 11, fontWeight: 700, color: T.textPrimary, marginBottom: 1, paddingLeft: 1 };

  // 月曆格子活動項目 — 當月換行顯示；非當月單行截斷
  const getCalendarActivityStyle = (color, isCurrentMonth) => ({
    fontSize: 9, lineHeight: 1.2, color: T.textPrimary,
    background: T.activityTagBg(color || DEFAULT_COLOR),
    borderRadius: 2, padding: '1px 2px', marginBottom: 1,
    overflow: 'hidden',
    cursor: 'pointer',
    ...(isCurrentMonth
      ? { whiteSpace: 'normal', wordBreak: 'break-word' }
      : { whiteSpace: 'nowrap', textOverflow: 'ellipsis' }),
  });

  return (
    <div style={pageStyle}>
      <header style={stickyHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
          <h1 style={{ ...mainTitleStyle, margin: 0 }}>齊文藝室活動曆</h1>
          {/* 深淺色切換 */}
          <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? '切換淺色模式' : '切換深色模式'}
            style={{
              background: isDark ? 'rgba(255,255,255,.15)' : 'rgba(94,58,26,.1)',
              border: 'none', borderRadius: 999,
              width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 18, lineHeight: 1,
              transition: 'background .2s',
            }}
          >
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
        <div style={{ textAlign: 'center', fontSize: 13, color: T.textMuted, marginBottom: 8 }}>
          齊文藝室共享咖啡廳-台北市中正區南陽街32號
        </div>

        {/* 第一行：標題 + 月/週切換膠囊按鈕 */}
        <div style={headerRowStyle}>
          <div style={subTitleStyle}>
            {viewMode === 'month' ? formatMonthLabel(currentMonth) : '本週活動'}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={viewMode === 'month' ? toggleBtnActive : toggleBtn} onClick={() => setViewMode('month')}>月</button>
            <button type="button" style={viewMode === 'week' ? toggleBtnActive : toggleBtn} onClick={() => setViewMode('week')}>週</button>
          </div>
        </div>

        {/* 第二行：上/下一月 or 上/下一週 */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {viewMode === 'month' ? (
            <>
              <button type="button" style={navBtn} onClick={goPrevMonth}>上個月</button>
              <button type="button" style={navBtn} onClick={goThisMonth}>本月</button>
              <button type="button" style={navBtn} onClick={goNextMonth}>下個月</button>
            </>
          ) : (
            <>
              <button type="button" style={navBtn} onClick={goPrevWeek}>上一週</button>
              <button type="button" style={navBtn} onClick={goThisWeek}>本週</button>
              <button type="button" style={navBtn} onClick={goNextWeek}>下一週</button>
            </>
          )}
          <a
            href="https://line.me/ti/g2/q37_e1fd1Leh2V8KwHbBDI0hacvc3SdFrqeX6g?utm_source=invitation&utm_medium=link_copy&utm_campaign=default"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...navBtn,
              padding: '8px 10px',
              background: '#06C755',
              color: '#fff',
              borderColor: '#06C755',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="#fff" style={{ flexShrink: 0 }}>
              <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .348-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .349-.281.631-.63.631h-2.386c-.345 0-.627-.282-.627-.631V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.627-.631.627-.346 0-.626-.283-.626-.627V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.627-.631.627-.345 0-.627-.283-.627-.627V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.627H4.917c-.345 0-.63-.282-.63-.627V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .349-.281.631-.629.631M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
            </svg>
            活動討論群
          </a>
          <a
            href={VENUE_INTRO_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...navBtn,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            活動場地介紹
          </a>
        </div>
      </header>

      <div style={contentStyle}>
        {deepLinkError && (
          <div style={{ margin: '12px 8px', padding: 12, background: isDark ? 'rgba(239,68,68,.2)' : '#fee', border: `1px solid ${isDark ? 'rgba(239,68,68,.4)' : '#fcc'}`, borderRadius: 8, color: isDark ? '#fca5a5' : '#b91c1c', fontSize: 14, textAlign: 'center' }}>
            {deepLinkError}
          </div>
        )}
        {loading ? (
          <p style={{ textAlign: 'center', padding: 24, color: T.loadingColor }}>載入中…</p>
        ) : viewMode === 'month' ? (
          <>
            {/* 場地顏色圖例 */}
            {venueColorLegend.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginBottom: 8, padding: '6px 4px' }}>
                {venueColorLegend.map((vc, idx) => (
                  <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.legendColor }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: vc.color, flexShrink: 0 }} />
                    {pickContentText(vc.short_name || vc.venue, vc.short_name_en || vc.venue_en)}
                  </span>
                ))}
              </div>
            )}
            <div style={calendarGridStyle}>
              {DAY_NAMES_SHORT.map(d => (
                <div key={d} style={calendarHeaderCell}>{d}</div>
              ))}
              {monthCalendar.map(({ ymd, day, isCurrentMonth }) => {
                const acts = activitiesByDate[ymd] || [];
                const hasActivity = acts.length > 0;
                const isToday = ymd === todayYmd;
                return (
                  <div key={ymd} style={calendarCell(isCurrentMonth, isToday, hasActivity)}>
                    <div style={calendarDayNum}>{day}</div>
                    {acts.map((a, idx) => (
                      <div
                        key={a.id || idx}
                        role="button"
                        tabIndex={0}
                        style={getCalendarActivityStyle(getVenueColor(a), isCurrentMonth)}
                        title={`${pickContentText(a.name, a.name_en)} ${formatActivityTimeRange(a.activity_time, a.end_time) || a.activity_time || ''}`}
                        onClick={(e) => { e.stopPropagation(); openDetail(a); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            openDetail(a);
                          }
                        }}
                      >
                        {pickContentText(a.name, a.name_en)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          /* 週模式 */
          <div>
            {weekDates.map((ymd, i) => (
              <div key={ymd} style={dayCardStyle}>
                <div style={dayTitleStyle}>
                  {DAY_NAMES[i]} <span style={{ fontSize: 20, fontWeight: 800 }}>{formatWeekDate(ymd)}</span>
                  {ymd === todayYmd && <span style={{ marginLeft: 8, fontSize: 13, color: T.todayAccent }}>今天</span>}
                </div>
                {(activitiesByDate[ymd] || []).length === 0 ? (
                  <div style={{ fontSize: 13, color: T.textDim, padding: '4px 0' }}>無活動</div>
                ) : (
                  (activitiesByDate[ymd] || []).map(a => (
                    <div key={a.id} style={weekActivityBlockStyle} onClick={() => openDetail(a)} role="button" tabIndex={0}>
                      <div style={activityRowStyle}>
                        <span style={{ fontSize: 14, color: T.textPrimary, fontWeight: 600 }}>
                          {formatActivityTimeRange(a.activity_time, a.end_time) || a.activity_time || ''}
                        </span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: T.textPrimary }}>{pickContentText(a.name, a.name_en)}</span>
                        <span style={{ fontSize: 13, color: T.textSecondary }}>場地：{pickContentText(a.venue, a.venue_en)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 活動詳情彈窗（月／週共用） */}
      {selectedActivity && (
        <Popup onClose={closeDetail} size="md" maxWidth="520px">
          <div style={{ padding: 8, position: 'relative' }}>
            <button
              type="button"
              onClick={closeDetail}
              style={{
                position: 'absolute', top: 0, right: 0,
                background: 'none', border: 'none',
                fontSize: 20, lineHeight: 1, color: isDark ? '#555' : T.textMuted,
                cursor: 'pointer', padding: '4px 8px',
              }}
              aria-label="關閉"
            >✕</button>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, paddingRight: 28 }}>{selectedActivity.name}</h2>
            {/* 圖片移到標題下方 */}
            {detailLoading ? (
              <p style={{ margin: '8px 0', color: '#888' }}>載入圖片…</p>
            ) : selectedActivity.images && selectedActivity.images.length > 0 ? (
              <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedActivity.images.map((url, idx) => (
                  <img key={idx} src={url} alt={`活動圖片 ${idx + 1}`} style={{ width: '100%', borderRadius: 8, display: 'block' }} />
                ))}
              </div>
            ) : null}
            {(() => {
              const timeText = formatActivityTimeRange(
                selectedActivity.activity_time,
                selectedActivity.end_time,
              ) || String(selectedActivity.activity_time || '').trim();
              return (
                <p style={{ margin: '4px 0', color: T.popupTextColor, fontSize: 14 }}>
                  {timeText}{selectedActivity.venue ? ` · 場地：${selectedActivity.venue}` : ''}
                </p>
              );
            })()}
            {selectedActivity.organizer && (
              <p style={{ margin: '4px 0', fontSize: 14 }}>主辦：{selectedActivity.organizer}</p>
            )}
            {selectedActivity.consumption_type ? (
              <p style={{ margin: '4px 0', fontSize: 14, color: '#666' }}>消費方式：{selectedActivity.consumption_type}</p>
            ) : null}
            {selectedActivity.detail ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>活動介紹</div>
                <div style={{ padding: 12, background: T.popupDetailBg, borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>
                  <Linkify text={selectedActivity.detail} linkColor={T.linkColor} />
                </div>
              </div>
            ) : null}
            {selectedActivity.activity_flow ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>活動流程</div>
                <div style={{ padding: 12, background: T.popupDetailBg, borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>
                  <Linkify text={selectedActivity.activity_flow} linkColor={T.linkColor} />
                </div>
              </div>
            ) : null}
            {selectedActivity.registration_link ? (
              <a
                href={selectedActivity.registration_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block', marginTop: 10, marginRight: 10,
                  padding: '10px 24px', background: '#1677ff', color: '#fff',
                  borderRadius: 8, fontSize: 15, fontWeight: 600, textDecoration: 'none',
                }}
              >
                報名連結
              </a>
            ) : null}
            {selectedActivity.registrationSlug && (
              <a href={`/venue/booking/p/${selectedActivity.registrationSlug}/form`}
                style={{ display: 'inline-block', marginTop: 12, padding: '10px 24px', background: '#531dab', color: '#fff', borderRadius: 8, fontSize: 15, fontWeight: 600, textDecoration: 'none', marginRight: 10 }}>
                立即報名
              </a>
            )}
            <button
              type="button"
              onClick={copyActivityLink}
              style={{
                marginTop: 12, marginRight: 10, padding: '8px 16px', borderRadius: 8,
                border: `1px solid ${T.popupBtnBorder}`, background: T.popupBtnBg,
                color: T.popupBtnColor, cursor: 'pointer', fontSize: 14
              }}
            >
              📋 複製活動連結
            </button>
            <button type="button" onClick={closeDetail} style={{ marginTop: 12, padding: '4px 14px', borderRadius: 8, border: `1px solid ${T.popupBtnBorder}`, background: T.popupBtnBg, color: T.popupBtnColor, cursor: 'pointer', fontSize: 14 }}>
              返回月曆
            </button>
          </div>
        </Popup>
      )}
    </div>
  );
}
